/* eslint-disable react-refresh/only-export-components */
import * as React from 'react'
import {
	Controller,
	ControllerProps,
	FieldPath,
	FieldValues,
	FormProvider,
	useFormContext
} from 'react-hook-form'

import * as LabelPrimitive from '@radix-ui/react-label'
import { Slot } from '@radix-ui/react-slot'

import InfoIcon from '@wwt/shared/assets/icons/outline/info.svg'
import WarningIcon from '@wwt/shared/assets/icons/outline/warning.svg'

import { cn } from '@/shared/lib/cn'
import { Label } from '@/shared/ui/Label'

export const Form = FormProvider

type FormFieldContextValue<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
	name: TName
}

export const FormFieldContext = React.createContext<FormFieldContextValue>(
	{} as FormFieldContextValue
)

export const FormField = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
	...props
}: ControllerProps<TFieldValues, TName>) => {
	return (
		<FormFieldContext value={{ name: props.name }}>
			<Controller {...props} />
		</FormFieldContext>
	)
}

type FormItemContextValue = {
	id: string
}

export const FormItemContext = React.createContext<FormItemContextValue>(
	{} as FormItemContextValue
)

export const useFormField = () => {
	const fieldContext = React.use(FormFieldContext)
	const itemContext = React.use(FormItemContext)
	const { getFieldState, formState } = useFormContext()

	const fieldState = getFieldState(fieldContext.name, formState)

	if (!fieldContext) {
		throw new Error('useFormField should be used within <FormField>')
	}

	const { id } = itemContext

	return {
		id,
		name: fieldContext.name,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
		...fieldState
	}
}

interface FormItemProps extends React.HTMLAttributes<HTMLDivElement> {
	ref?: React.RefObject<HTMLDivElement>
}

export const FormItem = ({ ref, className, ...props }: FormItemProps) => {
	const id = React.useId()

	return (
		<FormItemContext value={{ id }}>
			<div
				ref={ref}
				className={cn('space-y-1', className)}
				{...props}
			/>
		</FormItemContext>
	)
}
FormItem.displayName = 'FormItem'

export const FormLabel = ({
	ref,
	...props
}: React.ComponentPropsWithRef<typeof LabelPrimitive.Root>) => {
	const { formItemId } = useFormField()

	return (
		<Label
			ref={ref}
			htmlFor={formItemId}
			{...props}
		/>
	)
}
FormLabel.displayName = 'FormLabel'

export const FormControl = ({
	ref,
	...props
}: React.ComponentPropsWithRef<typeof Slot>) => {
	const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

	return (
		<Slot
			ref={ref}
			id={formItemId}
			aria-describedby={
				!error
					? `${formDescriptionId}`
					: `${formDescriptionId} ${formMessageId}`
			}
			aria-invalid={Boolean(error)}
			{...props}
		/>
	)
}
FormControl.displayName = 'FormControl'

type FormDescriptionProps = React.HTMLAttributes<HTMLParagraphElement> & {
	ref?: React.RefObject<HTMLParagraphElement>
}

export const FormDescription = ({
	ref,
	className,
	children,
	...props
}: FormDescriptionProps) => {
	const { error, formDescriptionId } = useFormField()

	return (
		<p
			ref={ref}
			id={formDescriptionId}
			className={cn(
				'flex gap-1 text-xs leading-4 [&>svg]:size-4',
				error && 'sr-only',
				className
			)}
			{...props}
		>
			<InfoIcon />
			{children}
		</p>
	)
}
FormDescription.displayName = 'FormDescription'

export interface FormMessageProps
	extends React.HTMLAttributes<HTMLParagraphElement> {
	ref?: React.RefObject<HTMLParagraphElement>
	errorMessage?: string
}

export const FormMessage = ({
	ref,
	className,
	children,
	errorMessage,
	...props
}: FormMessageProps) => {
	const { error, formMessageId } = useFormField()
	const body = error ? (
		<>
			<WarningIcon /> <span>{errorMessage ?? String(error?.message)}</span>
		</>
	) : (
		children
	)

	if (!body) {
		return null
	}

	return (
		<p
			ref={ref}
			id={formMessageId}
			className={cn(
				'flex gap-1 text-xs leading-4 [&>svg]:size-4',
				error && 'text-destructive',
				className
			)}
			{...props}
		>
			{body}
		</p>
	)
}
FormMessage.displayName = 'FormMessage'
